import { type FC } from "react";

import { VisuallyHidden } from "@mantine/core";
import { CheckIcon, CrossIcon, DotFilledIcon } from "@snapchat/snap-design-system-icons";
import { clsx } from "clsx";
import { type MessageDescriptor, useIntl } from "react-intl";

import { type SubmissionCheck, type SubmissionCheckState } from "../model";

import styles from "./WebLensPage.module.css";
import { messages } from "./messages.generated";

const ICON_SIZE = 16;

const STATE_ICON: Record<SubmissionCheckState, FC<{ className?: string; size?: number }>> = {
    pass: CheckIcon,
    fail: CrossIcon,
    // A running check is transient and carries its own progress detail, so it reads as pending
    // rather than earning a state of its own.
    running: DotFilledIcon,
    pending: DotFilledIcon,
};

const STATE_CLASS: Record<SubmissionCheckState, string> = {
    pass: styles.checkPass,
    fail: styles.checkFail,
    running: styles.checkRunning,
    pending: styles.checkPending,
};

// Colour and glyph are the only visible signal, so each row also states its outcome for
// screen readers.
const STATE_LABEL: Record<SubmissionCheckState, MessageDescriptor> = {
    pass: messages.checkStatePass,
    fail: messages.checkStateFail,
    running: messages.checkStatePending,
    pending: messages.checkStatePending,
};

/**
 * The submission rail's check list. Renders both the readiness rows a creator sees while filling
 * the form and the progress rows submit() writes, so a submission stays in one place on the page.
 */
export const SubmissionChecklist: FC<{ checks: SubmissionCheck[] }> = ({ checks }) => {
    const intl = useIntl();

    return (
        <ul className={styles.checklist} data-testid="web-lens-checklist">
            {checks.map((check) => {
                const StateIcon = STATE_ICON[check.state];

                return (
                    <li key={check.id} className={clsx(styles.check, STATE_CLASS[check.state])}>
                        <StateIcon className={styles.checkIcon} size={ICON_SIZE} />
                        <div>
                            <VisuallyHidden>{intl.formatMessage(STATE_LABEL[check.state])}</VisuallyHidden>
                            {check.label}
                            {check.detail ? <div className={styles.checkDetail}>{check.detail}</div> : null}
                            {check.issues && check.issues.length > 0 ? (
                                <ul className={styles.checkIssues}>
                                    {check.issues.map((issue, index) => (
                                        // Positional key: issues may share text.
                                        <li key={index}>{issue}</li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};
